import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createVendingSalesFormSchema } from '../validators/vendingSalesFormSchema';
import {
  buildAuditDynamicFormErrorState,
} from '../utils/auditDynamicFormUtils';
import {
  buildVendingSalesJsonModeFormData,
  cloneVendingInventoryLine,
  createEmptyVendingSalesLine,
  createVendingSalesFormDefaultValues,
} from '../utils/vendingSalesFormUtils';
import type { VendingSalesDynamicFormRHFNewProps, VendingSalesFormValues } from '../../../../types/vendingSalesFormTypes';

type UseVendingSalesDynamicFormLogicParams = Pick<
  VendingSalesDynamicFormRHFNewProps,
  'initialValues' | 'onSubmit' | 'onSwitchModes' | 'gamesData' | 'reportType'
>;

const getErrorAtPath = (errors: any, path: string): string | null => {
  const error = path.split('.').reduce((current, key) => current?.[key], errors);
  return typeof error?.message === 'string' ? error.message : null;
};

const buildVendingRequiredFieldMessage = (errors: any): string | null => {
  const messages = [
    getErrorAtPath(errors, 'location_key'),
    getErrorAtPath(errors, 'shift_date'),
    getErrorAtPath(errors, 'shift_start_time'),
  ].filter(Boolean);

  if (messages.length === 0) {
    return null;
  }

  return `Please select the required report fields before saving:\n${messages.map((message) => `- ${message}`).join('\n')}`;
};

export const useVendingSalesDynamicFormLogic = ({
  initialValues,
  onSubmit,
  onSwitchModes,
  gamesData,
  reportType,
}: UseVendingSalesDynamicFormLogicParams) => {
  const debugEnabled = useMemo(() => {
    try {
      return typeof window !== 'undefined' && window.localStorage?.getItem('debug_fix_ocr_vending') === '1';
    } catch {
      return false;
    }
  }, []);

  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState<VendingSalesFormValues>(initialValues);
  const isBinBasedReport = reportType === 'vending_inventory';

  useEffect(() => {
    setFormData(initialValues);
  }, [initialValues]);

  useEffect(() => {
    if (!debugEnabled) return;
    // eslint-disable-next-line no-console
    console.debug('[FixOCR][VendingForm] init', { reportType, initialValues });
  }, [debugEnabled, initialValues, reportType]);

  const schema = useMemo(() => createVendingSalesFormSchema(reportType), [reportType]);

  const baseResolver = useMemo(() => (
    yupResolver(
      schema,
      { abortEarly: false, context: { gamesData } } as any,
    ) as any
  ), [gamesData, schema]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<VendingSalesFormValues>({
    defaultValues: createVendingSalesFormDefaultValues(formData, reportType),
    resolver: useCallback(async (values: any, context: any, options: any) => {
      if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.debug('[FixOCR][VendingForm] resolver input', {
          reportType,
          initialValues: formData,
          values,
          context,
        });
      }

      const result = await baseResolver(values, context, options);

      if (debugEnabled) {
        // eslint-disable-next-line no-console
        console.debug('[FixOCR][VendingForm] resolver output', result);
      }

      return result;
    }, [baseResolver, debugEnabled, formData, reportType]),
  });

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: 'parsed_data.final_json.lines' as any,
  } as any);

  const reindexBins = useCallback(() => {
    if (!isBinBasedReport) return;

    const currentLines = getValues('parsed_data.final_json.lines' as any) as any[] | undefined;
    if (!Array.isArray(currentLines)) return;

    for (let idx = 0; idx < currentLines.length; idx += 1) {
      const expectedBin = idx + 1;
      const currentBin = (currentLines[idx] as any)?.bin;
      if (currentBin === expectedBin) continue;

      setValue(`parsed_data.final_json.lines.${idx}.bin`, expectedBin, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [getValues, isBinBasedReport, setValue]);

  const handleAppend = useCallback(() => {
    const currentLines = getValues('parsed_data.final_json.lines' as any) as any[] | undefined;
    const nextIndex = Array.isArray(currentLines) ? currentLines.length : 0;
    append(createEmptyVendingSalesLine(isBinBasedReport ? nextIndex + 1 : '') as any);
    Promise.resolve().then(reindexBins);
  }, [append, getValues, isBinBasedReport, reindexBins]);

  const handleInsert = useCallback((index: number) => {
    if (!isBinBasedReport) {
      insert(index, createEmptyVendingSalesLine('') as any);
      return;
    }

    const currentLines = getValues('parsed_data.final_json.lines' as any) as any[] | undefined;
    const preservedLines = Array.isArray(currentLines)
      ? currentLines.map((line) => cloneVendingInventoryLine(line))
      : [];

    insert(index, createEmptyVendingSalesLine(isBinBasedReport ? index + 1 : '') as any);
    Promise.resolve().then(() => {
      const restoredLines = [
        ...preservedLines.slice(0, index),
        createEmptyVendingSalesLine(index + 1),
        ...preservedLines.slice(index),
      ].map((line, lineIndex) => ({
        ...line,
        bin: lineIndex + 1,
      }));

      setValue('parsed_data.final_json.lines' as any, restoredLines as any, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
      reindexBins();
    });
  }, [getValues, insert, isBinBasedReport, reindexBins, setValue]);

  const handleRemove = useCallback((index: number) => {
    remove(index);
    Promise.resolve().then(reindexBins);
  }, [remove, reindexBins]);

  const handleSwitchToJson = useCallback(() => {
    const currentFormValues = getValues();
    onSwitchModes(buildVendingSalesJsonModeFormData(currentFormValues), 'json');
  }, [getValues, onSwitchModes]);

  const handleReject = useCallback(() => {
    const currentValues = getValues();
    onSubmit({
      ...currentValues,
      action: 'reject',
    });
  }, [getValues, onSubmit]);

  const handleCloseErrorDialog = useCallback(() => {
    setErrorPopupOpen(false);
  }, []);

  const handleFormSubmit = useCallback((data: VendingSalesFormValues) => {
    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.debug('[FixOCR][VendingForm] submit', { reportType, data });
    }
    onSubmit(data);
  }, [debugEnabled, onSubmit, reportType]);

  const handleSubmitError = useCallback((submitErrors: any) => {
    const currentValues = getValues();
    const { errorMessage: nextErrorMessage } = buildAuditDynamicFormErrorState(
      submitErrors,
      currentValues,
    );

    setErrorMessage(buildVendingRequiredFieldMessage(submitErrors) || nextErrorMessage);
    setErrorPopupOpen(true);
  }, [getValues]);

  return {
    control,
    handleSubmit,
    errors,
    setValue,
    getValues,
    fields,
    errorPopupOpen,
    errorMessage,
    handleAppend,
    handleInsert,
    handleRemove,
    handleSwitchToJson,
    handleReject,
    handleCloseErrorDialog,
    handleFormSubmit,
    handleSubmitError,
  };
};

export default useVendingSalesDynamicFormLogic;
